import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {ClickAwayListener, type SxProps} from '@mui/material';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import {lighten} from '@mui/system/colorManipulator';
import {type FC, useEffect, useMemo, useState} from 'react';
import {primaryColor, secondaryColor} from '@/theme/theme';

export type Option = {
  id: string;
  label: string;
};

export type MultiSelectFilterProps = {
  editable: boolean;
  options: Array<Option>;
  selectedOptionsOrientation?: 'column' | 'row';
  selectedOptions: Array<Option>;
  onOptionsChanged: (options: Array<Option>) => void;
  sx?: SxProps;
};

const MultiSelectEditableOnHover: FC<MultiSelectFilterProps> = ({
  editable,
  options,
  selectedOptions,
  selectedOptionsOrientation = 'column',
  onOptionsChanged,
}) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [newSelectedOptions, setNewSelectedOptions] = useState<Array<Option>>([]);

  useEffect(() => {
    setNewSelectedOptions(selectedOptions);
  }, [selectedOptions]);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  const noSelectedOptions = useMemo(() => {
    if (newSelectedOptions) {
      return options.filter((option) => !newSelectedOptions.some((sel) => sel.id === option.id));
    } else {
      return [];
    }
  }, [options, newSelectedOptions]);

  const hasChanges =
    newSelectedOptions.length !== selectedOptions.length ||
    newSelectedOptions.some((o) => !selectedOptions.find((s) => s.id === o.id));

  const handleBlur = () => {
    setEditing(false);
    if (hasChanges) {
      onOptionsChanged(newSelectedOptions);
    }
  };

  const toggleOption = (option: Option) => {
    setEditing(true);
    const exists = newSelectedOptions.some((o) => o.id === option.id);

    const updated = exists ? newSelectedOptions.filter((o) => o.id !== option.id) : [...newSelectedOptions, option];

    setNewSelectedOptions(updated);
  };

  return (
    <ClickAwayListener onClickAway={handleBlur}>
      <Stack
        direction={selectedOptionsOrientation}
        spacing={1}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={styles(hover, editing, editable).container}
      >
        <Stack
          direction="row"
          sx={styles(hover, editing, editable).content}
          onClick={() => editable && setEditing(true)}
        >
          <Stack direction="column" spacing={1}>
            {newSelectedOptions &&
              newSelectedOptions.length > 0 &&
              newSelectedOptions.map(({id, label}) => (
                <Chip
                  key={id}
                  label={label}
                  size="small"
                  variant="outlined"
                  onDelete={() => editable && toggleOption({id, label})}
                  sx={styles(hover, editing, editable).chip}
                  deleteIcon={hover || editing ? <ClearIcon /> : <DoneIcon />}
                />
              ))}
            {noSelectedOptions &&
              (hover || editing) &&
              noSelectedOptions.length > 0 &&
              noSelectedOptions.map(({id, label}) => (
                <Chip
                  key={id}
                  label={label}
                  size="small"
                  variant="outlined"
                  onDelete={() => toggleOption({id, label})}
                  deleteIcon={hover || editing ? <DoneIcon /> : <ClearIcon />}
                  sx={styles(hover, editing, editable).chipToInclude}
                />
              ))}
          </Stack>
          <EditOutlinedIcon color="primary" sx={styles(hover, editing, editable).icon} />
        </Stack>
      </Stack>
    </ClickAwayListener>
  );
};

export default MultiSelectEditableOnHover;

const styles = (hover: boolean, editing: boolean, editable: boolean): Record<string, SxProps> => ({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    border: hover || editing ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: hover || editing ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    p: 0.5,
    width: '100%',
  },
  content: {
    width: '100%',
    justifyContent: 'space-between',
  },
  chip: {
    borderRadius: 0,
    fontWeight: 700,
    color: hover || editing ? 'grey.700' : 'common.white',
    bgcolor: hover || editing ? lighten(primaryColor, 0.8) : 'primary.light',
    border: hover || editing ? '2px solid primary.main' : '0px solid red',
    '& .MuiChip-deleteIcon': {
      visibility: editable ? 'visible' : 'collapse',
      color: hover || editing ? 'primary.main' : 'common.white',
    },
  },
  chipToInclude: {
    borderRadius: 0,
    bgcolor: lighten(secondaryColor, 0.9),
    color: 'grey.700',
    '& .MuiChip-deleteIcon': {
      color: 'grey.700',
    },
  },
  icon: {
    opacity: hover || editing ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
  },
});
